import React, { useState } from 'react';
import { ChevronRightIcon, ChevronDownIcon, BookOpenIcon } from '@heroicons/react/24/outline';
import { HelpGuide as HelpGuideType, HelpGuideSection } from '../../types';

interface HelpGuideProps {
  guides: HelpGuideType[];
  searchQuery?: string;
}

interface GuideItemProps {
  guide: HelpGuideType;
  isExpanded: boolean;
  onToggle: () => void;
  searchQuery?: string;
}

interface GuideSectionProps {
  section: HelpGuideSection;
  searchQuery?: string;
}

const GuideSection: React.FC<GuideSectionProps> = ({ section, searchQuery }) => {
  // 検索クエリがある場合はハイライト表示
  const highlightText = (text: string, query?: string) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} className='bg-yellow-200 dark:bg-yellow-800 px-1 rounded'>
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  return (
    <div className='mb-6'>
      <h4 className='text-lg font-medium text-gray-800 dark:text-white mb-3'>
        {highlightText(section.title, searchQuery)}
      </h4>
      <div className='prose dark:prose-invert max-w-none text-gray-700 dark:text-gray-300'>
        <div
          dangerouslySetInnerHTML={{
            __html: section.content.replace(/\n/g, '<br />'),
          }}
        />
      </div>
      {section.subsections && section.subsections.length > 0 && (
        <div className='mt-4 pl-4 border-l-2 border-gray-200 dark:border-gray-700'>
          {section.subsections.map(subsection => (
            <div key={subsection.id} className='mb-4'>
              <h5 className='text-md font-medium text-gray-700 dark:text-gray-300 mb-2'>
                {highlightText(subsection.title, searchQuery)}
              </h5>
              <div className='prose dark:prose-invert max-w-none text-gray-600 dark:text-gray-400 text-sm'>
                <div
                  dangerouslySetInnerHTML={{
                    __html: subsection.content.replace(/\n/g, '<br />'),
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const GuideItem: React.FC<GuideItemProps> = ({ guide, isExpanded, onToggle, searchQuery }) => {
  return (
    <div className='border border-gray-200 dark:border-gray-700 rounded-lg mb-4'>
      <button
        onClick={onToggle}
        className='w-full p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:bg-gray-50 dark:focus:bg-gray-700'
        aria-expanded={isExpanded}
      >
        <div className='flex items-start justify-between'>
          <div className='flex items-start space-x-3 flex-1'>
            <div className='p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg flex-shrink-0'>
              <BookOpenIcon className='h-5 w-5 text-teal-600 dark:text-teal-400' />
            </div>
            <div className='flex-1'>
              <h3 className='text-lg font-medium text-gray-800 dark:text-white mb-1'>
                {guide.title}
              </h3>
              <p className='text-sm text-gray-600 dark:text-gray-400'>{guide.description}</p>
            </div>
          </div>
          {isExpanded ? (
            <ChevronDownIcon className='h-5 w-5 text-gray-500 flex-shrink-0 ml-2' />
          ) : (
            <ChevronRightIcon className='h-5 w-5 text-gray-500 flex-shrink-0 ml-2' />
          )}
        </div>
      </button>
      {isExpanded && (
        <div className='px-4 pb-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'>
          <div className='pt-4'>
            {guide.sections.map(section => (
              <GuideSection key={section.id} section={section} searchQuery={searchQuery} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const HelpGuide: React.FC<HelpGuideProps> = ({ guides, searchQuery }) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleExpanded = (guideId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(guideId)) {
      newExpanded.delete(guideId);
    } else {
      newExpanded.add(guideId);
    }
    setExpandedItems(newExpanded);
  };

  const expandAll = () => {
    setExpandedItems(new Set(guides.map(guide => guide.id)));
  };

  const collapseAll = () => {
    setExpandedItems(new Set());
  };

  if (guides.length === 0) {
    return (
      <div className='text-center py-8'>
        <p className='text-gray-500 dark:text-gray-400'>
          {searchQuery
            ? `「${searchQuery}」に関するガイドが見つかりませんでした。`
            : 'ガイドがありません。'}
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* ヘッダー */}
      <div className='flex justify-between items-center mb-4'>
        <h2 className='text-xl font-semibold text-gray-800 dark:text-white'>
          体系的ガイド
          {searchQuery && (
            <span className='text-sm font-normal text-gray-500 dark:text-gray-400 ml-2'>
              「{searchQuery}」の検索結果: {guides.length}件
            </span>
          )}
        </h2>
        <div className='flex space-x-2'>
          <button
            onClick={expandAll}
            className='text-sm text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-200'
          >
            すべて開く
          </button>
          <span className='text-gray-300 dark:text-gray-600'>|</span>
          <button
            onClick={collapseAll}
            className='text-sm text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-200'
          >
            すべて閉じる
          </button>
        </div>
      </div>

      {/* ガイド説明 */}
      <div className='bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-4 mb-6'>
        <div className='flex items-start space-x-3'>
          <BookOpenIcon className='h-5 w-5 text-teal-600 dark:text-teal-400 flex-shrink-0 mt-0.5' />
          <div>
            <h3 className='text-sm font-medium text-teal-800 dark:text-teal-200 mb-1'>
              体系的ガイドについて
            </h3>
            <p className='text-sm text-teal-700 dark:text-teal-300'>
              各機能の詳細な手順と使い方を、段階的に説明した包括的なガイドです。
              初心者から上級者まで、目的に応じて学習できます。
            </p>
          </div>
        </div>
      </div>

      {/* ガイド一覧 */}
      <div>
        {guides.map(guide => (
          <GuideItem
            key={guide.id}
            guide={guide}
            isExpanded={expandedItems.has(guide.id)}
            onToggle={() => toggleExpanded(guide.id)}
            searchQuery={searchQuery}
          />
        ))}
      </div>
    </div>
  );
};

export default HelpGuide;
